import {
  deleteField,
  duplicateField,
  fieldExists,
  getFieldName,
  getParentPathArray,
  makeUniqueFieldName,
  moveField,
  type PathArray,
} from "common/utils/formStructure";
import { Button } from "../../../../../../../Common/Buttons/Button";
import {
  getUiSchemaPropertyFormPath,
  useBuilderFormContext,
  useBuilderWatch,
} from "../../../form/form";
import { useDeleteModule } from "../../../modules/delete";
import { getModuleRequiringFieldInPath } from "../../../modules/utils";
import {
  SUBMISSIONS_BUILDER_MODE,
  useSubmissionsBuilderContext,
} from "../../../SubmissionsBuilderContext";
import { FieldActionsWrapper } from "./__styles__/FieldActions";
import { ToggleViewerPermissions } from "./ToggleViewerPermissions";

type FieldActionsProps = {
  fieldPath: PathArray;
  isGroup?: boolean;
};

export function FieldActions({ fieldPath, isGroup }: FieldActionsProps) {
  const { setValue, getValues, reset } = useBuilderFormContext();
  const { selectField, selectedField, mode } = useSubmissionsBuilderContext();
  const deleteModule = useDeleteModule();

  const isReadOnly = mode !== SUBMISSIONS_BUILDER_MODE.BUILD;

  const uiOrderPath = getUiSchemaPropertyFormPath(
    getParentPathArray(fieldPath),
    "order",
  );

  const uiOrder = (useBuilderWatch(uiOrderPath) ?? []) as Array<string>;
  const fieldName = getFieldName(fieldPath);
  const fieldIndex = fieldName ? uiOrder.indexOf(fieldName) : -1;

  const canMoveUp = fieldIndex > 0;
  const canMoveDown = fieldIndex !== -1 && fieldIndex < uiOrder.length - 1;

  function moveUp() {
    const previousSiblingFieldName = uiOrder[fieldIndex - 1];

    if (!canMoveUp || !previousSiblingFieldName) {
      return;
    }

    const targetPath = [
      ...getParentPathArray(fieldPath),
      previousSiblingFieldName,
    ];

    setValue(
      "formStructure",
      moveField({
        formStructure: getValues("formStructure"),
        sourcePath: fieldPath,
        targetPath,
      }),
      { shouldDirty: true },
    );
  }

  function moveDown() {
    const nextSiblingFieldName = uiOrder[fieldIndex + 1];

    if (!canMoveDown || !nextSiblingFieldName) {
      return;
    }

    const targetPath = [...getParentPathArray(fieldPath), nextSiblingFieldName];

    setValue(
      "formStructure",
      moveField({
        formStructure: getValues("formStructure"),
        sourcePath: fieldPath,
        targetPath,
      }),
      { shouldDirty: true },
    );
  }

  async function handleDelete() {
    const associatedModule = getModuleRequiringFieldInPath({
      fieldPath,
      formStructure: getValues("formStructure"),
      modules: getValues("modules"),
    });

    if (associatedModule) {
      await deleteModule(associatedModule);
    } else {
      handleDeleteField();
    }
  }

  function handleDeleteField() {
    const currentValues = getValues();
    const newFormStructure = deleteField({
      formStructure: currentValues.formStructure,
      targetPath: fieldPath,
    });

    if (selectedField && !fieldExists(newFormStructure.schema, selectedField)) {
      selectField(undefined);
    }

    reset(
      { ...currentValues, formStructure: newFormStructure },
      {
        keepDefaultValues: true,
        keepErrors: true,
      },
    );
  }

  const fieldPathTestId = fieldPath.join("-");

  return (
    <FieldActionsWrapper className="field-actions">
      {!isGroup && (
        <ToggleViewerPermissions
          fieldPath={fieldPath}
          isReadOnly={isReadOnly}
        />
      )}
      <Button
        size="small"
        icon="arrow-up"
        aria-label="Move up"
        data-testid={`move-up-${fieldPathTestId}`}
        variant="secondary"
        onPress={moveUp}
        isDisabled={!canMoveUp || isReadOnly}
      />
      <Button
        size="small"
        icon="arrow-down"
        aria-label="Move down"
        data-testid={`move-down-${fieldPathTestId}`}
        variant="secondary"
        onPress={moveDown}
        isDisabled={!canMoveDown || isReadOnly}
      />
      <Button
        size="small"
        icon="copy"
        aria-label="Duplicate field"
        data-testid={`duplicate-field-${fieldPathTestId}`}
        variant="secondary"
        onPress={() => {
          const newFieldName = makeUniqueFieldName();
          const newFormStructure = duplicateField({
            formStructure: getValues().formStructure,
            sourcePath: fieldPath,
            newFieldName,
          });
          setValue("formStructure", newFormStructure, { shouldDirty: true });
        }}
        isDisabled={isReadOnly}
      />
      <Button
        size="small"
        icon="trash-2"
        aria-label="Delete field"
        data-testid={`delete-field-${fieldPathTestId}`}
        variant="secondary"
        onPress={handleDelete}
        isDisabled={isReadOnly}
      />
    </FieldActionsWrapper>
  );
}
